import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { setColor } from "../../../../assets/styles/styles";
import Title from "../../components/Title";
import { default as Button } from "../../../../components/common/button/Button";
import { fetchAllVanityData } from "../../../../api/VanityLinkController";
import { getRoomById } from "../rooms/api/roomController";
import { getFileUrl } from "../../../../api/FilesController";
import { CACHE_QUERY_OPTIONS } from "../../../../constants";
import { formatDateTimeWithTimeZone, capitalizeFirstLetter } from "../../../../utils/generalUtils";
import SearchBar from "../../../../components/common/searchBar/SearchBar";
import Typography from "../../../../components/common/text/Typography";
import { ReactComponent as CalendarSVG } from "../../../../assets/images/icons/Calendar.svg";
import PinkIntraverseWithoutBorderLogo from "../../../../assets/images/logos/intraPinkWithoutBorder.png";
import Pagination from "../../../../components/common/pagination/Pagination";
import { ReactComponent as EditIcon } from "../../../../assets/images/icons/editIcon.svg";
import LoadingIcon from "../../../../components/common/loading/LoadingIcon";
import EditLinkModal from "./components/EditLink";
import CreateLinkModal from "./components/CreateLink";
import Modal from "../../components/Modal";

const VanityLinks = () => {
    const navigate = useNavigate();
    const userId = useSelector((state) => state?.user?.userId);
    const [filteredTableData, setFilteredTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalNumber, setTotalNumber] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [visibleRows, setVisibleRows] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [searchInput, setSearchInput] = useState("");
    const [isModalEditOpen, setIsModalEditOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const ROWS_PER_PAGE = 9;

    const {
        data,
        error,
        isLoading: dataLoading,
        refetch,
    } = useQuery(["vanityData"], () => fetchAllVanityData(), {
        ...CACHE_QUERY_OPTIONS(),
    });

    useEffect(() => {
        refetch();
    }, []);

    useEffect(() => {
        const fetchUpdateData = async () => {
            setIsLoading(true);

            if (!data || data.length === 0) {
                setIsLoading(false);
                return;
            }

            const updateData = await Promise.all(
                data.map(async (item) => {
                    const respData = await getRoomById(item?.roomId);
                    if (respData?.roomDetailsImageId !== undefined && respData?.roomDetailsImageId !== null) {
                        const getFile = await getFileUrl(respData?.roomDetailsImageId);
                        if (getFile?.status === 200) {
                            respData.roomDetailsImageId = getFile?.data;
                        }
                    }

                    return {
                        ...item,
                        createdAt: formatDateTimeWithTimeZone(item?.createdAt),
                        roomName: respData?.name,
                        roomDetailsImage: respData?.roomDetailsImageId,
                    };
                })
            );

            updateData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            let finalData = [...updateData];

            if (searchInput.length > 0) {
                const text = searchInput.toLowerCase();

                finalData = updateData.filter((item) => {
                    const vanityLink = item.vanityLink?.toLowerCase() || "";
                    const vanityId = item.vanityId?.toLowerCase() || "";
                    const roomName = item.roomName?.toLowerCase() || "";
                    const createdAt = item.createdAt?.toLowerCase() || "";

                    return (
                        vanityLink.includes(text) ||
                        vanityId.includes(text) ||
                        roomName.includes(text) ||
                        createdAt.includes(text)
                    );
                });

                setFilteredTableData(finalData);
            }

            setTotalNumber(finalData.length);
            setTotalPages(Math.ceil(finalData.length / ROWS_PER_PAGE));

            const startIdx = (currentPage - 1) * ROWS_PER_PAGE;
            const endIdx = currentPage * ROWS_PER_PAGE;

            setVisibleRows(finalData.slice(startIdx, endIdx));

            setIsLoading(false);
        };

        fetchUpdateData();
    }, [data, currentPage, searchInput]);

    const handleSearchChange = (value) => {
        setSearchInput(value); // Update search text state
    };

    const handleEdit = (item) => {
        setIsModalEditOpen(true);
        setSelectedRow(item);
    };

    return (
        <>
            <CardTable>
                <TableRow>
                    <Title
                        title={"Vanity Links"}
                        description={
                            "Manage all links in Intraverse. Changes made here are automatically pushed to related environments."
                        }
                    />
                    <TableColumn>
                        <Button
                            variant={"OutlinedButton"}
                            // disabled={successMessageBanner !== ""}
                            label={"Create Link"}
                            margin={"2rem 0 0 0"}
                            onClick={() => navigate("/console/vanity-links/create")}
                        />
                    </TableColumn>
                </TableRow>
            </CardTable>

            <FilterContainer>
                <SearchBar
                    width="400px"
                    placeholder="Search links by ID, title and creation date..."
                    onChange={handleSearchChange}
                />
            </FilterContainer>
            <FilterContainer>
                {searchInput.length !== 0 && filteredTableData.length === 0 && (
                    <MessageCenter signal>
                        <Typography variant="ConsoleDescription">Search Results:</Typography>
                        No forms found
                    </MessageCenter>
                )}
                {searchInput.length !== 0 && filteredTableData.length !== 0 && (
                    <MessageCenter>
                        <Typography variant="ConsoleDescription">Search Results:</Typography>
                        {filteredTableData.length === 1
                            ? ` ${filteredTableData.length} form found`
                            : `${filteredTableData.length} forms found`}
                    </MessageCenter>
                )}
            </FilterContainer>
            {isLoading && (
                <LoadingIconContainer>
                    {" "}
                    <LoadingIcon />
                </LoadingIconContainer>
            )}

            {!isLoading && visibleRows && visibleRows.length > 0 && (
                <>
                    <GridContainer>
                        {visibleRows.map((item, index) => (
                            <Card key={index}>
                                <EditIconContainer onClick={() => handleEdit(item)}>
                                    <StyledEditIcon />
                                </EditIconContainer>

                                <HeaderRow>
                                    <LogoWrapper>
                                        <MobileLogo
                                            src={
                                                item.roomDetailsImage
                                                    ? item.roomDetailsImage
                                                    : PinkIntraverseWithoutBorderLogo
                                            }
                                        />
                                    </LogoWrapper>
                                    <TitleCard>{item.roomName ? item.roomName : "No Room Name"}</TitleCard>
                                </HeaderRow>
                                <InfoRow>
                                    <b>Vanity Link:</b>
                                    {item.vanityLink}
                                </InfoRow>
                                <InfoRow>
                                    <b>Vanity ID:</b>
                                    {item.vanityId}
                                </InfoRow>
                                <InfoRow>
                                    <b>Room ID:</b>
                                    {item.roomId ? item.roomId : <StyledEditIcon />}
                                </InfoRow>
                                {item.staticUrl && (
                                    <InfoRow>
                                        <b>URL:</b>
                                        {item.staticUrl}
                                    </InfoRow>
                                )}
                                <InfoRow>
                                    <CalendarSVG />
                                    {item.createdAt}
                                </InfoRow>
                            </Card>
                        ))}
                    </GridContainer>
                    <Pagination
                        totalNumber={data?.length}
                        visibleRows={searchInput.length === 0 ? visibleRows : filteredTableData}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        totalPages={totalPages}
                        tag={"Vanity Links"}
                    />
                </>
            )}

            {isModalEditOpen && (
                <Modal
                    isOpen={isModalEditOpen}
                    setIsOpen={setIsModalEditOpen}
                    children={<EditLinkModal closeAddModal={() => setIsModalEditOpen(false)} data={selectedRow} />}
                />
            )}
        </>
    );
};

export default VanityLinks;

const LoadingIconContainer = styled.div`
    margin-top: 100px;
    display: flex;
    justify-content: center;
    width: 100%;
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 25px;
    margin: 20px 0;
    min-height: 100vh;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

const LogoWrapper = styled.div`
    width: 48px;
    height: 48px;
    background-color: #333; /* optional */
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MobileLogo = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const Card = styled.div`
    position: relative;
    background-color: rgb(23, 22, 34);
    color: #fff;
    border-radius: 12px;
    border: 1px solid ${setColor.modalOutline};
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 15px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.6);
    }
`;
const EditIconContainer = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    cursor: pointer;
    border-radius: 50%;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;
const TitleCard = styled.h2`
    font-size: 20px;
    font-weight: 600;
`;

const InfoRow = styled.div`
    display: flex;
    gap: 8px;
    font-size: 14px;
    color: #aaa;
`;

const FilterContainer = styled.div`
    display: flex;
    align-items: flex-start;
    width: 100%;
`;

const CardTable = styled.div`
    display: flex;
    flex-direction: column;
`;

const TableRow = styled.div`
    display: flex;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const TableColumn = styled.div`
    flex: 1;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;
const MessageCenter = styled.div`
    color: ${(props) => (props.signal ? setColor.errorColor : setColor.successColor)};
    margin: 1rem 0rem;
    display: flex;
    justify-content: flex-end;
`;
const StyledEditIcon = styled(EditIcon)`
    width: 25px;
    height: 25px;
    cursor: pointer;
`;
